import dateFormat from 'dateformat';
import { isThisYear, differenceInMonths, milliseconds, subDays } from 'date-fns';
import { dateTimeConfig } from './localization/config';
import { whitelabel } from './config/whitelabel';

export const getAuth = () => localStorage.getItem('auth');

export const getTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const getTimeZoneAbbreviation = () =>
  new Date().toLocaleTimeString(
    'en-us',
    {
      timeZone: getTimeZone(),
      timeZoneName: 'short'
    }
  ).split(' ')[2];

export const getRequestOptions = () => ({
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuth()}`,
    Timezone: getTimeZone()
  }
});

export const addParamsToUri = (uri, params) => {
  const keys = Object.keys(params);
  if (keys.length) {
    const p = [];
    keys.forEach(k => {
      if (typeof params[k] === 'boolean') {
        if (params[k]) {
          p.push(`${encodeURIComponent(k)}`);
        }
      } else {
        p.push(`${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`);
      }
    });
    if (p.length) {
      return `${uri}?${p.join('&')}`;
    }
  }
  return uri;
};

export const updateURLParameter = (paramName, paramValue) => {
  const url = new URL(window.location);
  url.searchParams.set(paramName, paramValue);
  window.history.pushState({}, '', url);
};

export const getURLParameter = paramName => {
  const urlParams = new URLSearchParams(window.location.search);
  const paramValue = urlParams.get(paramName);

  return paramValue;
};

const getWindowWidth = () => (
  window.innerWidth ||
  document.documentElement.clientWidth ||
  document.body.clientWidth
);

export const getWindowHeight = () => (
  window.innerHeight ||
  document.documentElement.clientHeight ||
  document.body.clientHeight
);

export const isWidth = width =>
  getWindowWidth() <= (width || 600);

export const listToTree = (arr, start = 0) => {
  const list = arr;
  const map = {};
  let node = null;
  const roots = [];
  let i = null;

  for (i = start; i < list.length; i += 1) {
    map[list[i].id] = i; // initialize the map
    list[i].children = []; // initialize the children
  }
  for (i = start; i < list.length; i += 1) {
    node = list[i];
    if (node.parentId !== start && list[map[node.parentId]]) {
      // if you have dangling branches check that map[node.parentId] exists
      list[map[node.parentId]].children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
};

export const uniq = (array, attrName) =>
  array.filter(function f(a) {
    return !this[a[attrName]] && (this[a[attrName]] = true);
  }, Object.create(null));

/**
 * Compares line segments.
 * Min should not be equal Max.
 * @param {number} min1
 * @param {number} max1
 * @param {number} min2
 * @param {number} max2
 * @returns {number} `0` OR `> 0`
 */
export const overlap = (min1, max1, min2, max2) =>
  Math.max(0, Math.min(max1, max2) - Math.max(min1, min2));

/**
 * Generate brightness of hex color
 * @param {string} color - hex color.
 * @param {number} amount - amount of brightness
 * @returns color brightness
 */

export const adjustColor = (color, amount) => {
  const hex = color
    .replace(/^#/, '')
    .replace(/../g, color2 =>
      `0${Math.min(255, Math.max(0, parseInt(color2, 16) + amount)).toString(16)}`.slice(-2));

  return `#${hex}`;
};

export const flatTreeProjects = (projects, level = 0) =>
  projects.reduce((a, c) => {
    a.push({ ...c, level });
    if (c.children) {
      a.push(...flatTreeProjects(c.children, level + 1));
    }
    return a;
  }, []);

/**
 * Returns date format based on unit system and format type
 * @param {string} destinationUintSystem - unit system to convert into
 * @param {string} type - format type "dateShort | dateYearShort |
 * dateTimeShort | dateTimePartial | dateTimePartialLeading0 | timeSpecifier | hourSpecifier | amPm
 * @returns {string | boolean} date format | specifier | isAmPm"
 */

export const getDateTimeFormat = (destinationUintSystem, type) => {
  if (dateTimeConfig[destinationUintSystem]) {
    if (dateTimeConfig[destinationUintSystem][type] !== undefined) {
      return dateTimeConfig[destinationUintSystem][type];
    }
    return dateTimeConfig[destinationUintSystem].dateShort;
  }
  if (dateTimeConfig.metric[type]) {
    return dateTimeConfig.metric[type];
  }
  return dateTimeConfig.metric.dateShort;
};

export const getFormattedDate = (timestamp, account) =>
  dateFormat(
    timestamp,
    getDateTimeFormat(
      account?.measurementSystem,
      isThisYear(new Date(timestamp)) ? 'dateTimePartial' : 'dateTimeShort'
    )
  );

export const getProjectDateRange = (p, destinationUintSystem, formatType, includeTimezone) => {
  const format = getDateTimeFormat(destinationUintSystem, formatType);
  const timeZone = getTimeZoneAbbreviation();

  if (p.expirationDate) {
    let startDate = `${dateFormat(new Date(p.beginDate), format)}`;
    let endDate = `${dateFormat(new Date(p.expirationDate), format)}`;

    if (includeTimezone) {
      startDate += ` (${timeZone})`;
      endDate += ` (${timeZone})`;
    }

    return `${startDate} – ${endDate}`;
  }

  const beginDate = new Date(p.beginDate);
  let startDate = `${dateFormat(beginDate, format)}`;

  if (includeTimezone) {
    startDate += ` (${timeZone})`;
  }

  return `Start${new Date() > beginDate ? 'ed' : 's'} on ${startDate}`;
};

export const getDeviceUrl = (id, date, tab = 90) => {
  const day = 1000 * 60 * 60 * 24;
  const start = +new Date(date) - day / 2;
  const end = +new Date(date) + day / 2;
  const deviceDate = `${new Date(start).toISOString()}/${new Date(end).toISOString()}`;

  return `/device/${id}/t/${tab}/dr/${deviceDate}`;
};

/**
 * Checking are dates in month range
 * @param {date | string} date1 - start date
 * @param {date | string} date2 - end date
 * @param {number} monthsRange - Months range
 * @returns {boolean} are dates in month range
 */
export const isInMonthRange = (date1, date2, monthsRange = 3) => {
  let start = date1;
  let end = date2;
  if (typeof start === 'string') start = new Date(start);
  if (typeof end === 'string') end = new Date(end);

  // allows selecting the first day of the next month
  end = subDays(end, 1);

  const monthDifference = differenceInMonths(end, start);
  return monthDifference <= (monthsRange - 1);
};

/**
 * Returns date range
 * @param {string} rangeStart - start date
 * @param {string} rangeEnd - end date
 * @returns {Object} date range object
 */

export const getDateRange = (rangeStart, rangeEnd) => {
  if (!(rangeStart && rangeEnd)) {
    return {
      end: '',
      start: ''
    };
  }

  const startDate = new Date(rangeStart);
  const endDate = new Date(rangeEnd);

  const startTimezoneHrs = startDate.getTimezoneOffset() / 60;
  const endTimezoneHrs = endDate.getTimezoneOffset() / 60;

  if (startTimezoneHrs === endTimezoneHrs) {
    return {
      end: dateFormat(new Date(endDate), 'isoUtcDateTime'),
      start: dateFormat(new Date(startDate), 'isoUtcDateTime')
    };
  }

  let end;
  const start = startDate;
  const timeZoneOffset =
    Math.max(startTimezoneHrs, endTimezoneHrs) -
    Math.min(startTimezoneHrs, endTimezoneHrs);

  if (startTimezoneHrs > endTimezoneHrs) {
    end = new Date(endDate.setHours(endDate.getHours() + Math.abs(timeZoneOffset)));
  } else {
    end = new Date(endDate.setHours(endDate.getHours() - Math.abs(timeZoneOffset)));
  }

  return {
    end: dateFormat(new Date(end), 'isoUtcDateTime'),
    start: dateFormat(new Date(start), 'isoUtcDateTime')
  };
};

export const isProjectExpired = p =>
  p.expirationDate && new Date(p.expirationDate) < new Date();

export function orderDevices(devices) {
  const activeDevices = devices.filter(d => !d.disabledBy);
  const disabledDevices = devices.filter(d => d.disabledBy);
  activeDevices.sort((a, b) => (a.title ?
    a.title.toLocaleLowerCase().localeCompare(b.title ?
      b.title.toLocaleLowerCase() : null) : null));
  disabledDevices.sort((a, b) => (a.title ?
    a.title.toLocaleLowerCase().localeCompare(b.title ?
      b.title.toLocaleLowerCase() : null) : null));
  return [...activeDevices, ...disabledDevices];
}

/**
 * Converts temperature from celsius to destination
 * measurement system temperature
 * @param {number | string} temperature - temperature value in °C
 * @param {string} destinationUintSystem - unit system to convert into
 * @returns {number} temperature
 */

export const convertTemperature = (temperature, destinationUintSystem) => {
  if (destinationUintSystem === 'us') {
    return Math.round(10 * (temperature * (9 / 5) + 32)) / 10;
  }
  return Number(temperature);
};

/**
 * Converts temperature from celsius to destination
 * measurement system temperature and vice versa
 * @param {number | string} temperature - temperature value
 * @param {("metric" | "us")} unitSystem - current unit system
 * @param {("metric" | "us")} destinationUintSystem - unit system to convert into
 * @param {number} decimalPlaces - number of decimal places
 * @returns {number} temperature
 */

export const convertTemperature1 =
  (temperature, unitSystem, destinationUintSystem, decimalPlaces) => {
    let result = Number(temperature);
    if (unitSystem === 'metric' && destinationUintSystem === 'us') {
      result = temperature * (9 / 5) + 32;
    }
    if (unitSystem === 'us' && destinationUintSystem === 'metric') {
      result = (temperature - 32) * (5 / 9);
    }
    if (decimalPlaces) {
      return Number(result.toFixed(decimalPlaces));
    }
    return result;
  };

/**
 * Converts temperature unit from celsius to destination
 * measurement system temperature unit
 * @param {string} destinationUintSystem - unit system to convert into
 * @returns {number} temperature
 */

export const convertTemperatureUnit = destinationUintSystem => {
  if (destinationUintSystem === 'us') {
    return '°F';
  }
  return '°C';
};

/**
 * Converts temperature from celsius to destination
 * measurement system temperature unit
 * @param {number | string} temperature - temperature value in °C
 * @param {string} destinationUintSystem - unit system to convert into
 * @param {number} decimalPlaces - number of decimal places
 * @returns {string} temperature string
 */

export const convertTemperatureToString = (temperature, destinationUintSystem, decimalPlaces) => {
  let convertedTemperature = convertTemperature(temperature, destinationUintSystem);
  if (decimalPlaces) {
    convertedTemperature = convertedTemperature.toFixed(decimalPlaces);
  }

  return `${convertedTemperature}${convertTemperatureUnit(destinationUintSystem)}`;
};

/**
 * Converts length from mm to in and vise versa
 * @param {number | string} length - length
 * @param {("metric" | "us")} unitSystem - current unit system
 * @param {("metric" | "us")} destinationUintSystem - unit system to convert into
 * @returns {number} converted length
 */

export const convertLength = (length, unitSystem, destinationUintSystem) => {
  if (unitSystem === 'metric' && destinationUintSystem === 'us') {
    return length / 25.4;
  }
  if (unitSystem === 'us' && destinationUintSystem === 'metric') {
    return 25.4 * length;
  }
  return Number(length);
};

/**
 * Returns length measurement unit based on measurement system
 * @param {("metric" | "us")} measurementSystem - measurement system
 * @returns {string} - length measurement unit
 */

export const getLengthUnit = measurementSystem => (measurementSystem === 'us' ? 'in' : 'mm');

/**
 * Converts weight from kg to lb and vise versa
 * @param {number | string} weight - weight
 * @param {("metric" | "us")} unitSystem - current unit system
 * @param {("metric" | "us")} destinationUintSystem - unit system to convert into
 * @returns {number} converted weight
 */

export const convertWeight = (weight, unitSystem, destinationUintSystem) => {
  if (unitSystem === 'metric' && destinationUintSystem === 'us') {
    return weight * 2.20462262185;
  }
  if (unitSystem === 'us' && destinationUintSystem === 'metric') {
    return weight / 2.20462262185;
  }
  return Number(weight);
};

/**
 * Returns weight measurement unit based on measurement system
 * @param {("metric" | "us")} measurementSystem - measurement system
 * @returns {string} - weight measurement unit
 */

export const getWeightUnit = measurementSystem => (measurementSystem === 'us' ? 'lb' : 'kg');

/**
 * Converts distance from meters to miles and vise versa
 * @param {number | string} distance - distance
 * @param {("metric" | "us")} unitSystem - current unit system
 * @param {("metric" | "us")} destinationUintSystem - unit system to convert into
 * @returns {number} converted distance
 */

export const convertDistance = (distance, unitSystem, destinationUintSystem) => {
  if (unitSystem === 'metric' && destinationUintSystem === 'us') {
    return Math.round(distance * 0.000621371 * 100) / 100;
  }
  if (unitSystem === 'us' && destinationUintSystem === 'metric') {
    return distance / 0.000621371;
  }
  return Number(distance);
};

/**
 * Returns distance measurement unit based on measurement system
 * @param {("metric" | "us")} measurementSystem - measurement system
 * @returns {string} - distance measurement unit
 */

export const getDistanceUnit = measurementSystem => (measurementSystem === 'us' ? 'mi' : 'm');

export function loadScript(uri, callback = () => null) {
  const s = document.createElement('script');
  s.src = uri;
  s.type = 'text/javascript';
  s.async = true;
  s.onload = callback;
  document.head.prepend(s);
}

export function loadLink(uri, callback = () => null) {
  const s = document.createElement('link');
  s.href = uri;
  s.rel = 'stylesheet';
  s.async = true;
  s.onload = callback;
  document.head.prepend(s);
}

export const setDocumentTitle = (title, additionalInfo) => {
  const MAX_CHAR_LENGTH = 21;
  let documentTitle = `${title} | ${whitelabel.getConfig().product}`;

  if (additionalInfo) {
    const info = additionalInfo.length > MAX_CHAR_LENGTH ?
      `${additionalInfo.substring(0, MAX_CHAR_LENGTH)}...` : additionalInfo;
    documentTitle = `${info} | ${documentTitle}`;
  }

  return document.title = documentTitle;
};

export const isUndefinedOrNull = value =>
  typeof value === 'undefined' || value === null;

/* eslint-disable no-mixed-operators */
export const uuidv4 = () =>
  (`${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));

export const formattedAddress = a => {
  const addressArray = ['address1', 'city', 'state', 'country'].reduce((acc, key) => {
    if (a[key]) {
      acc.push(a[key]);
    }
    return acc;
  }, []);

  return addressArray.join(', ');
};

/**
 * Sorts devices by title and/or uid.
 * @param {Array} devices - devices.
 * @returns {Array} sorted devices.
 */

export const sortDevices = (devices = []) => [...devices].sort((a, b) => {
  if (a.title && b.title) {
    return a.title > b.title ? 1 : -1;
  } if (a.title) {
    return -1;
  } if (b.title) {
    return 1;
  }
  return a.uid > b.uid ? 1 : -1;
});

/**
 * Sorts users by full name.
 * @param {Array} users - users.
 * @returns {Array} sorted users.
 */

export const sortUsers = users => [...users].sort((a, b) =>
  (a.firstName + a.lastName > b.firstName + b.lastName ?
    1 : b.firstName + b.lastName > a.firstName + a.lastName ? -1 : 0));

/**
 * Sorts projects by title.
 * @param {Array} projects - projects.
 * @returns {Array} sorted projects.
 */

export const sortProjects = projects => [...projects].sort((a, b) =>
  (a.title > b.title ? 1 : b.title > a.title ? -1 : 0));

/**
 * Returns line variant based on index.
 * @param {number} index - index.
 * @returns {string} line variant.
 */

export const getLineVariant = index => {
  let variant;
  switch (index) {
    case 1:
      variant = 'dashed';
      break;
    case 2:
      variant = 'dotted';
      break;
    case 3:
      variant = 'dash-dotted';
      break;
    default:
      variant = 'solid';
  }

  return variant;
};

export const getAdjustedLineColor = (sensorIndex, deviceColor) => {
  const evenIndex = sensorIndex % 2 === 0;
  const direction = evenIndex ? -1 : 1;

  return adjustColor(`#${deviceColor}`, direction * sensorIndex * 50);
};

export const getLinePointConfig = pointType => {
  let config = {};
  switch (pointType) {
    case 'rect':
      config = {
        pointStyle: 'rect'
      };
      break;
    case 'triangle':
      config = {
        pointStyle: 'triangle',
        pointBorderWidth: 2
      };
      break;
    default:
      config = {};
  }

  return config;
};

/**
 * Debounce function execution on timeout.
 * @param {func} fn - function to execute.
 * @param {number} timeout - timeout, default 100ms.
 * @returns {func} debounced function.
 */

export const debounce = (fn, timeout = 100) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fn, timeout, ...args);
  };
};

/**
 * Returns local decimal separator.
 * @returns {string} local decimal separator.
 */

export const getLocaleDecimalSeparator = () => {
  let n = 1.5;
  n = n.toLocaleString().substring(1, 2);
  return n;
};

/**
 * Returns devices metrics identifiers.
 * @param {Array} devices devices.
 * @param {object} meta - meta.
 * @returns {string[]} - metric identifiers
 */

export const getMetricsIdentifiers = (devices = [], meta = {}) => {
  const devicesTypes = devices.map(d => d.type);
  const devicesMeta = meta.device?.filter(m => devicesTypes.includes(m.name)) ?? [];
  const identifiers = devicesMeta.reduce((a, c) => {
    c.metrics.forEach(({ identifier }) => {
      if (!a.includes(identifier)) {
        a.push(identifier);
      }
    });
    return a;
  }, []);

  return identifiers;
};

/**
 * Checks if value is JS primitive.
 * @param {*} a - variable.
 * @returns {boolean}
 */

const isPrimitive = a => (a !== Object(a));

/**
 * Deeply compare 2 values
 * @param {*} a - value 1.
 * @param {*} b - value 2.
 * @returns {boolean}
 */

export const deepEqual = (a, b) => {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  if (typeof a === 'undefined' || typeof b === 'undefined') {
    return false;
  }

  if (isPrimitive(a) && isPrimitive(b)) {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) {
        return false;
      }
    }

    return true;
  }

  const aKeys = Object.keys(a);

  if (aKeys.length !== Object.keys(b).length) return false;

  for (let i = 0; i < aKeys.length; i += 1) {
    if (!Object.prototype.hasOwnProperty.call(b, aKeys[i])) {
      return false;
    }
    if (!deepEqual(a[aKeys[i]], b[aKeys[i]])) {
      return false;
    }
  }

  return true;
};

export const deepEqualWithoutOrder = (a, b) => {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  if (typeof a === 'undefined' || typeof b === 'undefined') {
    return false;
  }

  const primitive = value => value === null || (typeof value !== 'object' && typeof value !== 'function');

  if (primitive(a) && primitive(b)) {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;

    // Sort arrays before comparison
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    for (let i = 0; i < sortedA.length; i += 1) {
      if (!deepEqualWithoutOrder(sortedA[i], sortedB[i])) {
        return false;
      }
    }

    return true;
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    // Sort keys before comparison
    aKeys.sort();
    bKeys.sort();

    for (let i = 0; i < aKeys.length; i += 1) {
      if (aKeys[i] !== bKeys[i]) {
        return false;
      }
      if (!deepEqualWithoutOrder(a[aKeys[i]], b[bKeys[i]])) {
        return false;
      }
    }

    return true;
  }

  return false;
};

export const requestStatuses = Object.freeze({
  idle: 'idle',
  loading: 'loading',
  succeeded: 'succeeded',
  failed: 'failed'
});

export function momentFormat(date, account, formatType) {
  const d = new Date(date);
  const interval = new Date() - d;
  const min = 1000 * 60;

  return interval < min * 2 ? 'a minute ago' :
    interval < min * 58 ?
      `${Math.round(interval / min)} minutes ago` :
      interval < min * 65 ?
        'an hour ago' :
        dateFormat(
          date,
          getDateTimeFormat(account && account.measurementSystem, isThisYear(d) ? formatType : 'dateTimeShort')
        );
}

export const getMomentFormattedDate = (timestamp, account) => {
  if (!timestamp) return '';
  const formatType = isThisYear(new Date(timestamp)) ? 'dateTimePartial' : 'dateTimeShort';
  return momentFormat(timestamp, account, formatType);
};

/**
 * @typedef {Object} SimpleShipmentStatusObject
 * @property {number} code - shipment status code.
 * @property {string} label - shipment status label.
 */

/**
 * Returns shipment status.
 * @param {number} statusCode - status code.
 * @returns {SimpleShipmentStatusObject}
 */

export const getShipmentStatusByCode = statusCode => {
  const status = { code: statusCode };
  switch (statusCode) {
    case 100:
      status.label = 'Not started';
      break;
    case 200:
      status.label = 'In progress';
      break;
    case 300:
      status.label = 'Stopped';
      break;
    case 400:
      status.label = 'Done';
      break;
    default:
      status.label = '';
  }
  return status;
};

export const getAppBarHeight = () => {
  const offSetHeight = window.appBarRef?.current?.offsetHeight;

  return offSetHeight;
};

export const getOverviewHeaderHeight = () =>
  document.getElementById('overviewHeader')?.offsetHeight || 0;

/**
 * Writes a value to the host clipboard.
 *
 * @param {*} stringOrStringPromise - Either a string or a promise resolving to a string.
 * @returns
 */
export const writeToClipboard = stringOrStringPromise =>
  navigator.clipboard.write([
    new ClipboardItem({ 'text/plain': stringOrStringPromise })
  ]);

export function compareMetric(min, max, value) {
  if (value == null) {
    return false;
  }
  if (typeof min === 'number' && typeof max === 'number') {
    return value < min || value > max;
  }
  if (typeof min === 'number') {
    return value < min;
  }
  if (typeof max === 'number') {
    return value > max;
  }
  return false;
}

function buildExcursionObj(alert, metrics = { youngest: {} }) {
  const message = [];
  // youngest `_.timestamp` is epoch-seconds (see getYoungestMetrics); ×1000 so consumers can
  // do `new Date(timestamp)`. `unit`/`timestamp` keep parity with detectExcursions' output so
  // the same consumers (e.g. AlertIcon) can render either result.
  if (metrics.youngest.temperature?._) {
    const { _ } = metrics.youngest.temperature;
    message.push({
      id: 'temperature',
      label: 'Temperature',
      value: _.value,
      min: alert.tMin,
      max: alert.tMax,
      tab: 0,
      timestamp: _.timestamp ? _.timestamp * 1000 : undefined
    });
  }
  if (metrics.youngest.humidity?._) {
    const { _ } = metrics.youngest.humidity;
    message.push({
      id: 'humidity',
      label: 'Humidity',
      value: _.value,
      min: alert.hMin,
      max: alert.hMax,
      tab: 1,
      unit: '%',
      timestamp: _.timestamp ? _.timestamp * 1000 : undefined
    });
  }
  if (metrics.youngest.battery?._) {
    const { _ } = metrics.youngest.battery;
    message.push({
      id: 'battery',
      label: 'Battery',
      value: _.value,
      min: alert.bMin,
      tab: 2,
      unit: '%',
      timestamp: _.timestamp ? _.timestamp * 1000 : undefined
    });
  }

  return message;
}
/**
 * Detects the excursion in the last metric.
 * @param {object} alert setting.
 * @param {object} message last metric.
 * @returns {array}
 */
export function detectExcursion(alert, message) {
  const metrics = buildExcursionObj(alert, message);
  const excursions = [];

  metrics.forEach(metric => {
    const { min, max } = metric;
    if (typeof (min ?? max) === 'number') {
      if (compareMetric(min, max, metric.value)) {
        excursions.push({
          ...metric,
          cur: metric.value
        });
      }
    }
  });
  return excursions;
}

function buildExcursionsObj(alert, metric = {}) {
  let message = {};
  if (metric.id === 'temperature') {
    message = {
      id: 'temperature',
      label: 'Temperature',
      values: metric.values,
      min: alert.tMin,
      max: alert.tMax,
      tab: 0
    };
  }
  if (metric.id === 'humidity') {
    message = {
      id: 'humidity',
      label: 'Humidity',
      values: metric.values,
      min: alert.hMin,
      max: alert.hMax,
      tab: 1,
      unit: '%'
    };
  }
  if (metric.id === 'battery') {
    message = {
      id: 'battery',
      label: 'Battery',
      values: metric.values,
      min: alert.bMin,
      tab: 2,
      unit: '%'
    };
  }

  return message;
}

function getMetricKey(id, m) {
  let v = null;
  if (id === 'temperature') {
    v = m.temp_probe ?? m.temp ?? m.t_in;
  } else if (id === 'humidity') {
    v = m.humi;
  } else if (id === 'battery') {
    v = m.bat;
  }
  return v;
}

function detectMetricExcursion(min, max, id, values) {
  const { length } = values;
  for (let i = length - 1; i > -1; i -= 1) {
    const v = values[i];
    const value = getMetricKey(id, v);
    if (compareMetric(min, max, value)) {
      return {
        value,
        timestamp: v.timestamp
      };
    }
  }
  return null;
}
/**
 * Searches the excursion in the all metrics.
 * @param {object} alert setting.
 * @param {object} metrics
 * @returns {array}
 */
export function detectExcursions(alert, metrics = {}) {
  const excursions = [];

  Object.entries(metrics).forEach(([key, values]) => {
    const metric = buildExcursionsObj(alert, { id: key, values });
    const { min, max, id, label, tab, unit } = metric;
    if (typeof (min ?? max) === 'number') {
      const excursion = detectMetricExcursion(min, max, id, metric.values);
      if (excursion) {
        excursions.push({
          id,
          label,
          min,
          max,
          cur: excursion.value,
          ...excursion,
          tab,
          unit
        });
      }
    }
  });
  return excursions;
}

/**
 * Returns new device page route without projectId parameter. Should be removed later.
 * @param {string} url - old route url
 * @returns {string} - new route url
 */

export function getDeviceRouteWithoutProjectId(url) {
  const urlChunks = url.split('/');
  if (urlChunks.length > 0) {
    urlChunks.splice(2, 1);
    return urlChunks.join('/');
  }
  return url;
}

/**
 * Toggle root container class according to drawer state
 * @param {boolean} drawerState - drawer component state
 */

export function toggleRootContainerClass({ drawerState, withTransition }) {
  const rootContainer = document.getElementById('root');

  if (withTransition) {
    rootContainer.classList.add('transition');
  }

  if (drawerState) {
    rootContainer.classList.add('shifted');
    rootContainer.classList.remove('unshifted');
  } else {
    rootContainer.classList.add('unshifted');
    rootContainer.classList.remove('shifted');
  }
}

export const isTrailLabel = label => label === 'Audit trail';

/**
 * Returns object based on url search params.
 * @param {string} queryString - search query.
 * @returns {Object} Search query state object.
 */

export const parseQueryState = queryString => {
  const params = new URLSearchParams(queryString);

  return Object.fromEntries(params.entries());
};

/**
 * Remove object key if his value falsy.
 * @param {Object} obj object to be cleaned
 * @returns {Object} new cleaned object
 */

export const removeFalsyObjKey = obj => {
  const newObj = {};
  Object.keys(obj).forEach(key => {
    if (obj[key]) { newObj[key] = obj[key]; }
  });
  return newObj;
};

/**
 * Returns default map canter.
 * @param {Object} account object
 * @returns {Array} [lng, lat]
 */
export const getMapCenter = account =>
  (account?.measurementSystem === 'us' ? [-98.35, 39.50] : [8.496, 50.121]);

export const areAllObjectValuesEmpty = (obj = {}) =>
  Object.values(obj).every(value => value === null || value === undefined || value === '');

/**
 * @typedef {Object} TimeDeviation
 * @property {string|number} days
 * @property {string|number} hours
 * @property {string|number} minutes
 */

/**
 * Format timeDeviation into milliseconds
 * @param {TimeDeviation} timeDeviation
 * @returns {number} total milliseconds
 */
export const getMillisecondsFromTimeDelta = timeDeviation => {
  const formatDays = d => milliseconds({ days: d });
  const formatHours = h => milliseconds({ hours: h });
  const formatMinutes = m => milliseconds({ minutes: m });

  const formatterMap = {
    day: formatDays,
    hour: formatHours,
    minute: formatMinutes
  };

  const formattedRes = Object.keys(timeDeviation).reduce((acc, curr) => {
    const fn = formatterMap[curr];
    const value = timeDeviation[curr];

    if (fn && value) {
      const r = fn(+value);

      return acc + r;
    }

    return acc;
  }, 0);

  return formattedRes;
};

/**
 * Returns TimeDeviation from milliseconds
 * @param {number} t - milliseconds
 * @returns {TimeDeviation} timeDeviation
 */
export const getFullTimeFromTimeDelta = t => {
  const ONE_DAY = 24; // In hours
  const ONE_HOUR = 60; // In minutes
  const hInMilliseconds = 60 * 60 * 1000;
  const dInMilliseconds = 24 * 60 * 60 * 1000;

  if (t === 0) {
    return {
      day: '',
      hour: '',
      minute: 0
    };
  }

  let days = Math.floor(t / dInMilliseconds) || '';
  let hours = Math.floor((t - days * dInMilliseconds) / hInMilliseconds) || '';
  let minutes = Math.round((t - days * dInMilliseconds - hours * hInMilliseconds) / 60000) || '';

  if (minutes === ONE_HOUR) {
    hours += 1;
    minutes = 0;
  }

  if (hours === ONE_DAY) {
    days += 1;
    hours = 0;
  }

  return {
    day: days,
    hour: hours,
    minute: minutes
  };
};

export const getSearchParamsWithoutEmptyFields = searchParams => {
  const searchParamsString = searchParams.toString();

  searchParamsString.split('&').forEach(i => {
    const [key, value] = i.split('=');

    if (!value) {
      searchParams.delete(key);
    }
  });

  return searchParams.toString();
};

/**
 * Returns part of URL for an appliance
 * @param {string} timestamp - ISO Date
 * @param {string} creationDate - ISO Date
 * @returns {String}
 */
export const getLinkDateRange = (timestamp, creationDate) => {
  const day = 1000 * 60 * 60 * 24;
  const start = Math.max(+new Date(timestamp) - day / 2, +new Date(creationDate));
  const end = Math.min(+new Date(timestamp) + day / 2, +new Date());
  return `/dr/${new Date(start).toISOString()}/${new Date(end).toISOString()}`;
};

export const chunkArray = (array, chunkSize = 50) => {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
};

/**
 * Appends a comma-separated list of entity IDs to URL search params.
 * @param {URLSearchParams} searchParams - Mutable query string to update
 * @param {string} paramName - Query parameter name (e.g. `appliance`, `user`)
 * @param {Array<{ data: { id: string | number } }>} [values] - Selected filter options
 * @returns {void}
 */
export const setIdsParam = (searchParams, paramName, values) => {
  if (!values?.length) return;
  const ids = values.map(v => v.data.id).join(',');
  if (ids) searchParams.set(paramName, ids);
};
